import LinkOff from "@mui/icons-material/LinkOff";
import { Box, Button, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DateField } from "components/Forms/DateField";
import { FormMultiselect } from "components/Forms/FormMultiselect";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { EManifestWasteUnit, RcraEpisodicProjectType } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { selectItemsFromLabels } from "routes/Customer/Facility/ProfileV2/rcraProfileLabels";

import { EPISODIC_PROJECT_TYPE_LABELS, WASTE_UNIT_LABELS } from "../../rcraNotificationLabels";
import { SubRecordDialog, SubRecordGrid } from "../subRecordListEditor";
import { SubRecordRow, useSubRecordEditor } from "../useSubRecordEditor";
import { emptyWasteLine } from "./buildInputs";
import {
  DATE_PICKER_PROPS,
  gridSx,
  PLANNED_PROJECT_TYPE_PREFIX,
  YES_NO_OPTIONS,
} from "./constants";
import { EmergencyContactPrefill } from "./EmergencyContactPrefill";
import { validateOptionalPositiveNumber } from "./fieldRules";
import { SectionHeader } from "./SectionHeader";
import { EpisodicWasteLineForm, FilingNotificationsFormData } from "./types";
import { WasteCodesField } from "./WasteCodesField";
import { getInvalidFederalWasteCodes, getInvalidStateWasteCodes } from "./wasteCodeValidation";

/** §13 Episodic Generation. */
export function EpisodicGenerationSection({ facilityId }: { facilityId: string }) {
  const { control, metadata } = useValidatingFormContext<FilingNotificationsFormData>();
  const isDisplayOnly = metadata?.isDisplayOnly ?? false;
  const { watch, getValues, setValue } = useFormContext<FilingNotificationsFormData>();
  const gate = watch("episodicGate");

  // The facility state unlocks that state's wildcards (e.g. TX
  // facility-specific codes) during state-code validation; RCRA stepper
  // submissions are single-facility.
  const { submission } = useWasteStepper();
  const facilityState = submission?.facilitySubmissions?.[0]?.facility?.state ?? null;
  const getInvalidStateCodes = useCallback(
    (codes: string[]) => getInvalidStateWasteCodes(codes, facilityState),
    [facilityState]
  );

  // Row-level flag for the grid: an error icon whose tooltip names the bad
  // codes; the dialog's red chips carry the per-code detail.
  const getRowIssue = useCallback(
    (row: EpisodicWasteLineForm) => {
      const invalid = [
        ...getInvalidFederalWasteCodes(row.federalWasteCodes ?? []),
        ...getInvalidStateCodes(row.stateWasteCodes ?? []),
      ];
      if (invalid.length === 0) {
        return null;
      }
      return `Invalid waste code(s): ${invalid.map(({ code }) => code).join(", ")}`;
    },
    [getInvalidStateCodes]
  );

  // A locked emergency contact carries a linked roster Person id: its free-text
  // fields render read-only and the id round-trips on save. "Use a different
  // contact" clears the link and the free-text values, returning the fields to
  // manual entry.
  const emergencyContactPersonId = watch("emergencyContactPersonId");
  const isEmergencyContactLocked = emergencyContactPersonId != null;
  const handleUnlinkEmergencyContact = useCallback(() => {
    const opts = { shouldDirty: true, shouldValidate: true } as const;
    setValue("emergencyContactPersonId", null, opts);
    setValue("emergencyContactFirst", null, opts);
    setValue("emergencyContactLast", null, opts);
    setValue("emergencyContactEmail", null, opts);
    setValue("emergencyContactPhone", null, opts);
  }, [setValue]);

  // The 8700-12 only asks filers to describe the event when the "Other" project
  // type (planned or unplanned) is selected — gate the field to match the form.
  const projectTypes = watch("projectTypes") ?? [];
  const showOtherDescription =
    projectTypes.includes(RcraEpisodicProjectType.UnplannedOther) ||
    projectTypes.includes(RcraEpisodicProjectType.PlannedOther);

  // Project types are gated on the planned/unplanned answer: only the matching
  // set is offered (planned events show only PLANNED_* types, and vice versa).
  // Until the question is answered there is nothing to choose from.
  const isPlanned = watch("isPlannedEpisodicEvent");
  const projectTypeItems = useMemo(() => {
    const all = selectItemsFromLabels<RcraEpisodicProjectType>(EPISODIC_PROJECT_TYPE_LABELS);
    if (isPlanned == null) {
      return [];
    }
    return all.filter(
      (item) => item.value.startsWith(PLANNED_PROJECT_TYPE_PREFIX) === (isPlanned === true)
    );
  }, [isPlanned]);

  // When the planned/unplanned answer changes, drop any selected project types
  // that no longer belong to the chosen set so the form never holds an option
  // the user can't see (which would otherwise fail the CE match rule silently).
  useEffect(() => {
    if (isPlanned == null) {
      return;
    }
    const current = getValues("projectTypes") ?? [];
    const matching = current.filter(
      (type) => type.startsWith(PLANNED_PROJECT_TYPE_PREFIX) === (isPlanned === true)
    );
    if (matching.length !== current.length) {
      setValue("projectTypes", matching, { shouldDirty: true });
    }
  }, [isPlanned, getValues, setValue]);

  const wasteLines = watch("wasteLines") ?? [];
  const { append, remove, update } = useFieldArray<FilingNotificationsFormData, "wasteLines">({
    control,
    name: "wasteLines",
  });

  const editor = useSubRecordEditor<EpisodicWasteLineForm>({
    items: wasteLines,
    append: (value) => append(value),
    update: (index, value) => update(index, value),
    remove,
    getItem: (index) => getValues(`wasteLines.${index}`),
  });

  const columns: GridColDef<SubRecordRow<EpisodicWasteLineForm>>[] = [
    {
      field: "wasteDescription",
      headerName: "Waste Description",
      flex: 1.5,
      valueGetter: (_, row) => row.wasteDescription ?? "",
    },
    {
      field: "estimatedQuantity",
      headerName: "Estimated Quantity",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) =>
        row.estimatedQuantity
          ? `${row.estimatedQuantity} ${WASTE_UNIT_LABELS[row.quantityUnit]}`
          : "",
    },
    {
      field: "federalWasteCodes",
      headerName: "Federal Codes",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => (row.federalWasteCodes ?? []).join(", "),
    },
    {
      field: "stateWasteCodes",
      headerName: "State Codes",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => (row.stateWasteCodes ?? []).join(", "),
    },
  ];

  return (
    <Box sx={gridSx}>
      <Box sx={{ gridColumn: "span 12" }}>
        <SectionHeader label="Episodic Generation (§13)" />
      </Box>
      <Box sx={{ gridColumn: "span 12" }}>
        <RadioGroupField
          label="Did this site have an episodic generation event this reporting period?"
          name="episodicGate"
          control={control}
          radioOptions={YES_NO_OPTIONS}
        />
      </Box>
      {gate === true && (
        <>
          <Box sx={{ gridColumn: "span 12" }}>
            <RadioGroupField
              label="Was this a planned episodic event?"
              name="isPlannedEpisodicEvent"
              control={control}
              radioOptions={YES_NO_OPTIONS}
            />
          </Box>
          {isPlanned != null && (
            <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
              <FormMultiselect
                label="Project types"
                name="projectTypes"
                control={control}
                selectItems={projectTypeItems}
                selectProps={{ multiple: true }}
              />
            </Box>
          )}
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}>
            <DateField
              label="Beginning date"
              name="beginningDate"
              control={control}
              datePickerProps={DATE_PICKER_PROPS}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 3" } }}>
            <DateField
              label="End date"
              name="endDate"
              control={control}
              datePickerProps={DATE_PICKER_PROPS}
            />
          </Box>
          {showOtherDescription && (
            <Box sx={{ gridColumn: "span 12" }}>
              <FormTextField
                name="otherDescription"
                label="Describe other event"
                control={control}
              />
            </Box>
          )}
          <Box sx={{ gridColumn: "span 12", mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                Emergency contact
              </Typography>
              {!isDisplayOnly &&
                (isEmergencyContactLocked ? (
                  <Button
                    size="small"
                    startIcon={<LinkOff />}
                    onClick={handleUnlinkEmergencyContact}
                  >
                    Use a different contact
                  </Button>
                ) : (
                  <EmergencyContactPrefill facilityId={facilityId} />
                ))}
            </Stack>
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name="emergencyContactFirst"
              label="First name"
              control={control}
              disabled={isEmergencyContactLocked}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name="emergencyContactLast"
              label="Last name"
              control={control}
              disabled={isEmergencyContactLocked}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name="emergencyContactEmail"
              label="Email"
              control={control}
              disabled={isEmergencyContactLocked}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name="emergencyContactPhone"
              label="Phone"
              control={control}
              disabled={isEmergencyContactLocked}
            />
          </Box>
          <SubRecordGrid<EpisodicWasteLineForm>
            rows={editor.rows}
            dataColumns={columns}
            listLabel="Episodic waste lines"
            addLabel="Add episodic waste line"
            isDisplayOnly={isDisplayOnly}
            onAdd={editor.openAdd}
            onEdit={editor.openEdit}
            rowToDelete={editor.rowToDelete}
            onRequestDelete={editor.requestDelete}
            onConfirmDelete={editor.confirmDelete}
            deleteHeader="Remove this waste line?"
            renderDeleteLabel={(row) => row.wasteDescription ?? "Unnamed waste line"}
            getRowIssue={getRowIssue}
          />
        </>
      )}
      {editor.isOpen && (
        <SubRecordDialog<EpisodicWasteLineForm>
          title={editor.isAdding ? "Add episodic waste line" : "Edit episodic waste line"}
          gridSx={gridSx}
          initialValues={editor.editingValues ?? emptyWasteLine()}
          isDisplayOnly={isDisplayOnly}
          onCancel={editor.close}
          onCommit={editor.commit}
          renderFields={(dialogForm) => (
            <>
              <Box sx={{ gridColumn: "span 12" }}>
                <FormTextField
                  name="wasteDescription"
                  label="Waste description"
                  control={dialogForm.control}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <FormTextField
                  name="estimatedQuantity"
                  label="Estimated quantity"
                  control={dialogForm.control}
                  rules={{ validate: validateOptionalPositiveNumber }}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <FormSelect
                  label="Quantity unit"
                  name="quantityUnit"
                  control={dialogForm.control}
                  selectItems={selectItemsFromLabels<EManifestWasteUnit>(WASTE_UNIT_LABELS)}
                  clearable={false}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <WasteCodesField
                  name="federalWasteCodes"
                  label="Federal waste codes"
                  control={dialogForm.control}
                  disabled={isDisplayOnly}
                  getInvalidCodes={getInvalidFederalWasteCodes}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                <WasteCodesField
                  name="stateWasteCodes"
                  label="State waste codes"
                  control={dialogForm.control}
                  disabled={isDisplayOnly}
                  getInvalidCodes={getInvalidStateCodes}
                />
              </Box>
            </>
          )}
        />
      )}
    </Box>
  );
}
